// Port de la partie anime-sama d'api.go.

import { animesamaBase, normalizeEmbedHost } from "../constants.ts";
import { fetchText, proxyFetch } from "../proxy.ts";
import { embedPriority, isSupportedEmbed } from "../resolvers.ts";

export interface AnimeHit {
  name: string;
  url: string;
}

export interface Season {
  name: string;
  path: string;
}

// Le domaine n'est connu qu'une fois `/config` lu : il se relit à chaque appel
// plutôt que d'être figé au chargement du module.
const referer = (): string => animesamaBase() + "/";

export async function search(
  query: string,
  signal?: AbortSignal,
): Promise<AnimeHit[]> {
  const res = await proxyFetch(animesamaBase() + "/template-php/defaut/fetch.php", {
    referer: referer(),
    method: "POST",
    body: new URLSearchParams({ query }).toString(),
    contentType: "application/x-www-form-urlencoded",
    signal,
  });

  if (!res.ok) return [];

  const doc = new DOMParser().parseFromString(await res.text(), "text/html");
  const hits = new Map<string, string>();

  for (const anchor of doc.querySelectorAll("a[href]")) {
    for (const title of anchor.querySelectorAll("h3")) {
      const name = title.textContent?.trim();
      if (name) hits.set(name, anchor.getAttribute("href")!);
    }
  }

  return [...hits].map(([name, url]) => ({ name, url }));
}

const PANNEAU_RE = /panneauAnime\s*\(\s*"([^"]*)"\s*,\s*"([^"]*)"\s*\)/g;
const COMMENT_RE = /\/\*[\s\S]*?\*\//g;

export async function seasons(animeUrl: string): Promise<Season[]> {
  const html = (await fetchText(animeUrl, { referer: referer() })).replace(
    COMMENT_RE,
    "",
  );

  const found = new Map<string, string>();

  for (const match of html.matchAll(PANNEAU_RE)) {
    found.set(match[1]!, match[2]!);
  }

  return [...found]
    .map(([name, path]) => ({ name, path }))
    .sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

const EPS_BLOCK_RE = /var\s+(eps\d+)\s*=\s*\[([\s\S]*?)\];/g;
const EPS_ENTRY_RE = /['"]\s*(https?:\/\/[^'"]+?)\s*['"]/g;

/** Ordonne les lecteurs : eps2 est le lecteur "myTV" habituel, puis eps1, puis
 *  les suivants dans l'ordre du site. */
function epsPriority(name: string): number {
  if (name === "eps2") return 0;
  if (name === "eps1") return 1;

  return 1 + (Number.parseInt(name.slice("eps".length), 10) || 0);
}

/** Retourne, par index d'épisode, tous les lecteurs supportés proposés pour cet
 *  épisode, du plus au moins prioritaire.
 *
 *  anime-sama publie un tableau epsN par lecteur, tous alignés sur le même
 *  index d'épisode : on ne peut donc pas se contenter de filtrer les URLs
 *  supportées, sous peine de décaler la numérotation dès qu'un bloc mélange les
 *  hôtes. */
export function playerCandidates(js: string): string[][] {
  const blocks = new Map<string, string[]>();
  let size = 0;

  for (const block of js.matchAll(EPS_BLOCK_RE)) {
    const entries = [...block[2]!.matchAll(EPS_ENTRY_RE)].map((entry) => entry[1]!);

    blocks.set(block[1]!, entries);
    size = Math.max(size, entries.length);
  }

  const names = [...blocks.keys()].sort((a, b) => epsPriority(a) - epsPriority(b));

  const episodes: string[][] = [];
  let last = -1;

  for (let i = 0; i < size; i++) {
    const seen = new Set<string>();
    const candidates: string[] = [];

    for (const name of names) {
      const entry = blocks.get(name)![i];
      if (entry === undefined) continue;

      const embed = normalizeEmbedHost(entry);

      if (!isSupportedEmbed(embed) || seen.has(embed)) continue;

      seen.add(embed);
      candidates.push(embed);
    }

    // les lecteurs d'un même épisode sont essayés dans l'ordre de préférence
    candidates.sort((a, b) => embedPriority(a) - embedPriority(b));

    episodes.push(candidates);

    if (candidates.length > 0) last = i;
  }

  // on s'arrête au dernier épisode réellement jouable
  return episodes.slice(0, last + 1);
}

export async function episodes(
  catalogueUrl: string,
  seasonPath: string,
): Promise<string[][]> {
  const js = await fetchText(`${catalogueUrl}/${seasonPath}/episodes.js`, {
    referer: referer(),
  });

  if (js === "") return [];

  return playerCandidates(js);
}
